import type { GaApp } from '~/modules/app'
import type { UserData } from '~/modules/main'
import type { NotificationSettings } from '~/modules/notifications'
import type { ProductSku } from '~/modules/product'

import type {
  AppInfoData,
  BridgeIncomingPayloads,
  BridgeOutgoingPayloads,
  EventListener,
  MessageEvent,
  Unsubscribe,
} from '../constant'
import { EVENT_TO_DISPATCH, EVENT_TO_LISTEN, MESSAGE_EVENT } from '../constant'

export class BridgeService {
  gaApp: GaApp
  private listeners = new Map<string, Set<EventListener<unknown>>>()

  constructor(gaApp: GaApp) {
    this.gaApp = gaApp
  }

  /** Инициализация прослушки сообщений */
  public async init(): Promise<void> {
    globalThis.addEventListener(MESSAGE_EVENT, this.onMessage)

    await this.sendGetAppInfoMessage()
  }

  /** Отписка от событий */
  public unsubscribe(): void {
    globalThis.removeEventListener(MESSAGE_EVENT, this.onMessage)

    this.listeners.clear()
  }

  /**
   * Базовый метод отправки сообщения
   */
  private send<K extends keyof BridgeOutgoingPayloads>(
    name: K,
    data?: BridgeOutgoingPayloads[K],
  ): void {
    if (window.parent !== window) {
      window.parent.postMessage(
        data !== undefined ? { name, data } : { name },
        '*',
      )
    }
  }

  /** Обработка входящего сообщения */
  private handleOnMessage(name: string, data: unknown): void {
    switch (name) {
      case EVENT_TO_LISTEN.USER:
        void this.gaApp.services.main.user.processUserData(data as UserData)
        break
      case EVENT_TO_LISTEN.GET_PARAMS:
        // TODO: дополнить событие GET_PARAMS
        this.gaApp.services.app.main.processGetParams()
        break
      case EVENT_TO_LISTEN.APP_INFO:
        this.gaApp.services.app.main.processAppInfo(data as AppInfoData)
        break
      case EVENT_TO_LISTEN.ADD_TO_FAVORITES_SUCCESS:
        this.gaApp.services.product.favorites.setItem((data as ProductSku).sku)
        break
      case EVENT_TO_LISTEN.REMOVE_FROM_FAVORITES_SUCCESS:
        this.gaApp.services.product.favorites.removeItem(
          (data as ProductSku).sku,
        )
        break
      default:
        console.warn('Unknown event type:', name)
        break
    }
  }

  /**
   * Обработка входящего сообщения
   */
  private readonly onMessage = (event: MessageEvent): void => {
    const eventData = event.data ?? {}
    const name = eventData?.name ?? ''
    const data = eventData?.data ?? {}

    if (typeof name !== 'string' || !name.length) return

    this.handleOnMessage(name, data)

    const handlers = this.listeners.get(name)
    handlers?.forEach((handler) => handler(data))
  }

  /**
   * Подписка на событие
   * @returns Функция отписки
   */
  public on<K extends keyof BridgeIncomingPayloads>(
    eventName: K,
    callback: EventListener<BridgeIncomingPayloads[K]>,
  ): Unsubscribe {
    if (!this.listeners.has(eventName)) {
      this.listeners.set(eventName, new Set())
    }

    const handlers = this.listeners.get(eventName)!
    handlers.add(callback as EventListener<unknown>)

    return () => handlers.delete(callback as EventListener<unknown>)
  }

  /**
   * Подписка на событие с ожиданием
   * @param eventName Название ивента, который ждем в ответ
   * @param predicate Функция для валидации данных события. Если возвращает false, ожидание продолжается до получения валидных данных
   */
  private async waitFor<K extends keyof BridgeIncomingPayloads>(
    eventName: K,
    predicate?: (data: BridgeIncomingPayloads[K]) => boolean,
  ): Promise<BridgeIncomingPayloads[K]> {
    return new Promise((resolve) => {
      const unsubscribe = this.on(eventName, (data) => {
        if (predicate && !predicate(data)) return
        unsubscribe()
        resolve(data)
      })
    })
  }

  /**
   * Отправка сообщения за базовыми данными стенда
   */
  public async sendGetAppInfoMessage() {
    this.send(EVENT_TO_DISPATCH.GET_APP_INFO)

    return this.waitFor(EVENT_TO_LISTEN.APP_INFO)
  }

  /**
   * Отправка сообщения за параметрами
   */
  public async sendGetParamsMessage() {
    this.send(EVENT_TO_DISPATCH.GET_PARAMS)

    return this.waitFor(EVENT_TO_LISTEN.GET_PARAMS)
  }

  /**
   * Отправка сообщения за данными пользователя
   */
  public async sendGetUserMessage() {
    this.send(EVENT_TO_DISPATCH.GET_USER)

    return this.waitFor(EVENT_TO_LISTEN.USER)
  }

  /**
   * Отправка сообщения о проверке заполненности данных пользователя
   */
  public async sendCheckUserDataFilledMessage() {
    this.send(EVENT_TO_DISPATCH.CHECK_USER_DATA_FILLED)

    return this.waitFor(EVENT_TO_LISTEN.CHECK_USER_DATA_FILLED)
  }

  /**
   * Отправка сообщения о необходимости авторизации
   */
  public sendAuthMessage() {
    this.send(EVENT_TO_DISPATCH.AUTH)
  }

  /**
   * Отправка сообщения шаринга
   */
  public sendShareMessage(url: string) {
    this.send(EVENT_TO_DISPATCH.SHARE, { url })
  }

  /**
   * Отправка сообщения открытия ссылки
   */
  public sendOpenUrlMessage(url: string) {
    this.send(EVENT_TO_DISPATCH.OPEN, { url })
  }

  /**
   * Отправка сообщения перезагрузки
   */
  public sendReloadMessage() {
    this.send(EVENT_TO_DISPATCH.RELOAD)
  }

  /**
   * Отправка сообщения обновления токена
   */
  public sendRefreshTokenMessage() {
    this.send(EVENT_TO_DISPATCH.REFRESH_TOKEN)
  }

  /**
   * Отправка сообщения добавления продукта в корзину
   */
  public sendAddToCartMessage(sku: string) {
    this.send(EVENT_TO_DISPATCH.ADD_TO_CART, { sku })
  }

  /**
   * Отправка сообщения добавления продукта в избранное
   */
  public async sendAddToFavoritesMessage(sku: string) {
    this.send(EVENT_TO_DISPATCH.ADD_TO_FAVORITES, { sku })

    return this.waitFor(
      EVENT_TO_LISTEN.ADD_TO_FAVORITES_SUCCESS,
      (data) => data.sku === sku,
    )
  }

  /**
   * Отправка сообщения запроса SKU товаров в корзине
   */
  public async sendGetCartItemsSkuMessage() {
    this.send(EVENT_TO_DISPATCH.GET_CART_ITEMS_SKU)
    return this.waitFor(EVENT_TO_LISTEN.GET_CART_ITEMS_SKU_SUCCESS)
  }

  /**
   * Отправка сообщения переключения локали
   */
  public sendSwitchLocaleMessage(code: string) {
    this.send(EVENT_TO_DISPATCH.SWITCH_LOCALE, { code })
  }

  /**
   * Отправка сообщения удаления из избранного
   */
  public async sendRemoveFromFavoritesMessage(sku: string) {
    this.send(EVENT_TO_DISPATCH.REMOVE_FROM_FAVORITES, { sku })
    return this.waitFor(
      EVENT_TO_LISTEN.REMOVE_FROM_FAVORITES_SUCCESS,
      (data) => data.sku === sku,
    )
  }

  /**
   * Отправка сообщения отображения нотификации
   */
  public sendShowNotificationMessage(settings: NotificationSettings) {
    this.send(EVENT_TO_DISPATCH.SHOW_NOTIFICATION, {
      ...settings,
      hasCloseButton: !!settings.hasCloseButton,
    })
  }

  /**
   * Отправка сообщения закрытия вкладки.
   * @param {string} [url] полный URL; если не передан — родитель откроет главную страницу
   */
  sendCloseMessage(url: string) {
    this.send(EVENT_TO_DISPATCH.CLOSE, { url })
  }

  /** Отправка сообщения выхода из игры */
  public sendExitMessage(url: string) {
    this.sendCloseMessage(url)
  }
}
